import {
  all,
  call,
  take,
  takeEvery,
  takeLatest,
  delay,
  put,
  race,
  select,
} from 'redux-saga/effects';

import { checkNetworkStatus } from '../../utils';
import config from '../../config';
import { REFRESH_VALUES } from '../application';
import { getCurrentUser } from '../auth/selectors';
import { getSelf as getSelfApi, getNotificationConfigs } from '../../services';
import { getActiveSite } from '../sites';
import { getUserNotificationConfigs } from '../notification-config/selectors';
import {
  RECEIVE_NOTIFICATION,
  START_NOTIFICATION_TIMER,
  CANCEL_NOTIFICATION_TIMER,
  START_POLLING_ONLINE,
  STOP_POLLING_ONLINE,
  CHECK_ONLINE,
  TYPES,
  setOnline,
  stopPollingOnline,
  setOffline,
  startPollingOnline,
  removeNotification,
  hideNotification,
} from './actions';
import { getOnline, getNotificationById } from './selectors';

let lastNetworkCheck = 0;

export function* delayedHideOrRemove(id, hideTimeout = 2000) {
  yield delay(hideTimeout);
  const notification = yield select(getNotificationById, id);
  yield notification?.type === TYPES.transient
    ? put(removeNotification(id))
    : put(hideNotification(id));
}

export function* doStartNotificationTimer({ id, hideTimeout }) {
  yield race([call(delayedHideOrRemove, id, hideTimeout), take(CANCEL_NOTIFICATION_TIMER)]);
}

export function* pollReconnect() {
  while (true) {
    const online = yield call(checkNetworkStatus);
    if (online) {
      yield put(setOnline());
      yield put({ type: REFRESH_VALUES });
      yield put(stopPollingOnline());
    }
    yield delay(5000);
  }
}

export function* watchStartPollingOnline() {
  while (true) {
    yield take(START_POLLING_ONLINE);
    yield race([call(pollReconnect), take(STOP_POLLING_ONLINE)]);
  }
}

export function* checkOnline() {
  const isOnlineState = yield select(getOnline);
  if (isOnlineState && lastNetworkCheck + 5000 < Date.now()) {
    lastNetworkCheck = Date.now();
    const online = yield call(checkNetworkStatus);
    if (!online) {
      yield put(setOffline());
      yield put(startPollingOnline());
    }
  }
}

const STATUS_SEVERITY_MAP = {
  alert: 'Alert',
  warning: 'Warning',
  normal: 'Normal',
};

// Cache the resolved role config to avoid 3 API calls (role configs + site members
// + org members) on every incoming notification. Invalidated on site change;
// role config changes mid-session require a page refresh to take effect.
let cachedConfig = null;
let cachedSiteId = null;

export function getAppEnabledForSeverity(config, severity) {
  if (!config) return true;
  return config.stateChangeSettings?.[severity]?.app === true;
}

export function* resolveCurrentUserConfig(providedSite) {
  const currentUser = yield select(getCurrentUser);
  if (!currentUser) return null;

  const userConfigs = yield select(getUserNotificationConfigs);
  const userConfig = userConfigs.find((c) => c.user === currentUser.subject);
  if (userConfig) return userConfig;

  const site = providedSite || (yield select(getActiveSite));
  if (!site?.id) return null;

  // Invalidate cache on site change
  if (cachedSiteId !== site.id) {
    cachedConfig = null;
    cachedSiteId = site.id;
  }

  if (cachedConfig) return cachedConfig;

  // Fetch user configs, role configs, and site members in parallel.
  // This avoids a race condition where alarms arrive before
  // syncNotificationConfigs populates Redux.
  const [{ data: fetchedUserConfigs }, { data: roleConfigs }, self] = yield all([
    call(getNotificationConfigs, { org: site.org, site: site.id, configType: 'user' }),
    call(getNotificationConfigs, { org: site.org, site: site.id, configType: 'role' }),
    call(getSelfApi, { withSiteMemberId: true, site: site.id }),
  ]);

  const fetchedUserConfig = fetchedUserConfigs?.find((c) => c.user === currentUser.subject);
  if (fetchedUserConfig) {
    return fetchedUserConfig;
  }

  return roleConfigs.find((c) => c.role === self.memberId) || null;
}

// Global filter: suppress an in-app alarm notification whose severity
// has app: false in the user's Transformer Alarms notification config.
export function* doFilterNotification(action) {
  if (config.onPremise) return;

  const { notification } = action;
  if (!notification) return;
  if (notification.category !== 'alarms') return;

  const severity = STATUS_SEVERITY_MAP[notification.status];
  if (!severity) return;

  const userConfig = yield call(resolveCurrentUserConfig);
  if (!getAppEnabledForSeverity(userConfig, severity)) {
    yield put(removeNotification(notification.id));
  }
}

export const sagas = [
  call(watchStartPollingOnline),
  takeEvery(START_NOTIFICATION_TIMER, doStartNotificationTimer),
  takeLatest(CHECK_ONLINE, checkOnline),
  takeEvery(RECEIVE_NOTIFICATION, doFilterNotification),
];
